import React, { useState, useEffect } from "react";
import { Tooltip } from "react-tooltip";
import axios from "axios";
import { PDFDocument } from "pdf-lib";
import { parsePageRangeString } from "./ReportPdf";
import { API_BASE_URL } from "../../global";

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;

const HyperLinkReport = ({ range, caseId, caseName, onClose }) => {
  const pages = parsePageRangeString(range);
  const [zoom, setZoom] = useState(1);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const zoomIn = () =>
    setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () =>
    setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  const resetZoom = () => setZoom(1);

  // Build a standalone PDF containing only the referenced pages, so the
  // native iframe viewer (and its zoom) only ever shows that range.
  useEffect(() => {
    if (!caseId || pages.length === 0) {
      setBlobUrl(null);
      return;
    }

    let cancelled = false;
    let objectUrl = null;

    const buildRangePdf = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.post(`${API_BASE_URL}get_pages`, {
          case_id: caseId,
          pages,
        });

        if (response.data.status !== 1) {
          throw new Error(response.data.message || "Failed to fetch pages");
        }

        const orderedPages = [...response.data.pdf_pages].sort(
          (a, b) => a.page_no - b.page_no
        );

        const mergedPdf = await PDFDocument.create();
        for (const { page_url } of orderedPages) {
          // Use fetch (not axios) so the global auth-token interceptor doesn't
          // attach an Authorization header to this third-party file request —
          // that header triggers a CORS preflight the file host rejects.
          const pageRes = await fetch(page_url);
          if (!pageRes.ok) {
            throw new Error(`Failed to fetch page (${pageRes.status})`);
          }
          const data = await pageRes.arrayBuffer();
          const singlePagePdf = await PDFDocument.load(data, {
            ignoreEncryption: true,
          });
          const [copiedPage] = await mergedPdf.copyPages(singlePagePdf, [0]);
          mergedPdf.addPage(copiedPage);
        }

        const mergedBytes = await mergedPdf.save();
        if (cancelled) return;

        objectUrl = URL.createObjectURL(
          new Blob([mergedBytes], { type: "application/pdf" })
        );
        setBlobUrl(objectUrl);
      } catch (err) {
        console.error("Error building page-range preview:", err);
        if (!cancelled) {
          setError(
            `Failed to load preview: ${err?.message || "unknown error"}`
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    buildRangePdf();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [caseId, range]);

  // Native #zoom= fragment helps in Chrome; our own transform scale is the real fallback.
  const iframeSrc = blobUrl
    ? `${blobUrl}#zoom=${Math.round(zoom * 100)}&toolbar=1&navpanes=0`
    : null;

  return (
    <div className="" id="">
      <div className="div3-header d-flex justify-content-between align-items-center">
        <h4>{caseName}</h4>
        <Tooltip id="hyperlink-report-close" className="tooltip-custom" />
        <button
          id="closeBtn"
          onClick={onClose}
          className="btn data-btn close-btn"
          data-tooltip-id="hyperlink-report-close"
          data-tooltip-content={"Close"}
        >
          <i className="icofont-close-line"></i>
        </button>
      </div>
      <div className="main-ctn-wrapper" id="mainCtnWrapper">
        <div className="ai-response-area">
          <div className="ai-report" id="docPreview">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>
                👁️ Document Preview
                {pages.length > 0
                  ? ` (Page${pages.length > 1 ? "s" : ""} ${range})`
                  : ""}
              </h4>
              {iframeSrc && (
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn data-btn"
                    onClick={zoomOut}
                    disabled={zoom <= ZOOM_MIN}
                    title="Zoom out"
                  >
                    <i className="icofont-minus"></i>
                  </button>
                  <span style={{ minWidth: 48, textAlign: "center" }}>
                    {Math.round(zoom * 100)}%
                  </span>
                  <button
                    type="button"
                    className="btn data-btn"
                    onClick={zoomIn}
                    disabled={zoom >= ZOOM_MAX}
                    title="Zoom in"
                  >
                    <i className="icofont-plus"></i>
                  </button>
                  <button
                    type="button"
                    className="btn data-btn"
                    onClick={resetZoom}
                    title="Reset zoom"
                  >
                    Reset
                  </button>
                </div>
              )}
            </div>
            <div
              className="doc-preview-wrap"
              style={{
                overflow: "auto",
                width: "100%",
                height: "70vh",
                border: "1px solid #ddd",
              }}
            >
              {loading ? (
                <div className="text-muted">Loading preview…</div>
              ) : error ? (
                <div className="text-muted">{error}</div>
              ) : pages.length === 0 ? (
                <div className="text-muted">No page range selected.</div>
              ) : !blobUrl ? (
                <div className="text-muted">
                  Missing case ID — cannot load preview.
                </div>
              ) : (
                <div style={{ width: `${zoom * 100}%`, height: `${zoom * 100}%` }}>
                  <iframe
                    key={iframeSrc}
                    src={iframeSrc}
                    title="PDF preview"
                    style={{
                      width: `${100 / zoom}%`,
                      height: `${100 / zoom}%`,
                      transform: `scale(${zoom})`,
                      transformOrigin: "top left",
                      border: "none",
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HyperLinkReport;
